import { useState } from 'react';

import { Stack } from '@mui/material';

import {
  resetExceptSegments,
  selectScenarioDaysOfWeekInfo,
  selectScenarioEnd,
  selectScenarioIsSaving,
  selectScenarioName,
  selectScenarioStart,
  selectScenarioStatus,
} from 'state/detourSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';

import { PROCESS_STATUS, ScenarioSetupUIMode } from 'appConstants';
import { StlButton } from 'features/common/StlButton/StlButton';
import HexSpinner from 'features/spinner/HexSpinner';

import {
  selectProject,
  selectScenarioSetupUIMode,
  selectSegmentId,
  selectTypicalMonth,
  setTypicalMonth,
} from '../../state/workflowSlice';
import { useGetScenarioPeriodsQuery } from './ScenarioMonthPicker';
import ScenarioCreatePopup from './popups/ScenarioCreatePopup';
import ScenarioEditPopup from './popups/ScenarioEditPopup';
import { validateClosureDuration } from './utils';

import styles from './WorkflowDetour.module.css';

interface IScenarioCreateButtons {
  disableRun?: boolean;
  disableClear?: boolean;
}

export default function ScenarioCreateButtons({
  disableRun = false,
  disableClear = false,
}: IScenarioCreateButtons) {
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const scenarioIsSaving = useAppSelector(selectScenarioIsSaving);
  const scenarioName = useAppSelector(selectScenarioName);
  const { startDay, endDay } = useAppSelector(selectScenarioDaysOfWeekInfo);
  const closureStart = useAppSelector(selectScenarioStart);
  const closureEnd = useAppSelector(selectScenarioEnd);
  const selectedSegmentId = useAppSelector(selectSegmentId);
  const project_slug = useAppSelector(selectProject);
  const typicalMonth = useAppSelector(selectTypicalMonth);
  const scenarioStatus = useAppSelector(selectScenarioStatus);
  const uiMode = useAppSelector(selectScenarioSetupUIMode);

  const dispatch = useAppDispatch();

  const { data: detourMonthsInfo } = useGetScenarioPeriodsQuery(project_slug, {
    skip: !project_slug,
  });
  const shouldShowLoading =
    scenarioIsSaving ||
    (scenarioStatus === PROCESS_STATUS.PROCESSING &&
      uiMode === ScenarioSetupUIMode.Scenarios);

  const hasData = Boolean(
    startDay ||
    endDay ||
    scenarioName ||
    closureStart ||
    closureEnd ||
    typicalMonth !== detourMonthsInfo?.[0].typicalMonth,
  );

  const clearForm = () => {
    dispatch(setTypicalMonth(detourMonthsInfo?.[0].typicalMonth));
    dispatch(resetExceptSegments());
  };

  const calculateClick = () => {
    if (uiMode === ScenarioSetupUIMode.Create) setShowCreateModal(true);
    else if (uiMode === ScenarioSetupUIMode.Edit) setShowEditModal(true);
  };
  const durationValid =
    startDay &&
    endDay &&
    closureStart &&
    closureEnd &&
    !validateClosureDuration(startDay, endDay, closureStart, closureEnd);

  const runScenarioActive = Boolean(
    !scenarioIsSaving &&
    durationValid &&
    selectedSegmentId &&
    scenarioName &&
    !disableRun,
  );
  const clearActive =
    !disableClear && !scenarioIsSaving && hasData && !shouldShowLoading;

  return (
    <>
      <Stack className={styles.stack_buttons}>
        <StlButton
          value="Clear"
          isActive={clearActive}
          handleClick={clearForm}
          width={122}
          variant="outlined"
        />
        <StlButton
          isActive={runScenarioActive}
          icon={shouldShowLoading && <HexSpinner size={16} />}
          value={shouldShowLoading ? 'Running' : 'Run Scenario'}
          sx={
            shouldShowLoading && {
              backgroundColor: '#4D4D4D',
              color: '#fff !important',
            }
          }
          handleClick={calculateClick}
          width={122}
        />
      </Stack>
      <ScenarioCreatePopup
        open={showCreateModal}
        setOpen={setShowCreateModal}
      />
      <ScenarioEditPopup open={showEditModal} setOpen={setShowEditModal} />
    </>
  );
}
